import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';
import { UserPlusIcon, BoltIcon, TrophyIcon } from '../components/icons';
import { useAuthContext } from '../contexts/AuthContext';

export default function HowItWorksPage() {
  const { isAuthenticated } = useAuthContext();
  useDocumentMeta(
    'How It Works',
    'Step-by-step guide to playing Takedown League: create an account, join a league, draft NCAA wrestlers using a budget system, and compete based on real tournament results.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Play Takedown League',
    description:
      'A step-by-step guide to setting up your fantasy wrestling league, drafting NCAA wrestlers, and competing based on real tournament results.',
    totalTime: 'PT5M',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Create Your Account',
        text: 'Sign up for Takedown League using your email address or Google account. Account creation is free and takes less than two minutes.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Create or Join a League',
        text: 'Create a league for an NCAA wrestling tournament or join an existing league using an invite link or join code.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Draft Wrestlers to Your Roster',
        text: 'Use your budget to draft up to 10 NCAA wrestlers — one per weight class. Wrestler costs are based on their tournament seed.',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Earn Points from Tournament Results',
        text: 'Your score is the total NCAA team points earned by your drafted wrestlers through placement, advancement, and bonus points.',
      },
      {
        '@type': 'HowToStep',
        position: 5,
        name: 'Check Standings and Win',
        text: 'Track your league standings in real time as the tournament progresses. The member with the highest score wins.',
      },
    ],
  });

  return (
    <PublicLayout>
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">How Takedown League Works</h1>
          <p className="text-xl text-purple-100">
            A complete guide to setting up your league and competing with friends.
          </p>
        </div>
      </div>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Getting Started</h2>
          <p className="text-gray-700 mb-6">
            Takedown League is a fantasy wrestling platform for NCAA tournaments. You draft real
            wrestlers using a budget system and earn points based on the team points they accumulate
            during the tournament. Here is everything you need to know to get started.
          </p>

          <div className="space-y-10">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Your Account</h3>
                <p className="text-gray-700 mb-3">
                  Sign up for Takedown League using your email address or Google account. Account
                  creation is free and only requires a valid email. Once registered, you can set a
                  display name that will be visible to other league members. Your account is your
                  home base for managing all of your leagues, rosters, and standings.
                </p>
                <p className="text-gray-700">
                  After signing up, you will receive a confirmation email. Enter the code from that
                  email to verify your account and you are ready to go. If you sign up with Google,
                  verification is handled automatically.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Create or Join a League
                </h3>
                <p className="text-gray-700 mb-3">
                  A league is a group of people competing against each other around a specific NCAA
                  tournament. We support Regional (national qualifier) and National tournaments. As
                  soon as the seeds are determined for a tournament, you can create a league for it.
                  You can participate in as many leagues as you wish, and you can even have multiple
                  team entries in the same league.
                </p>
                <p className="text-gray-700 mb-3">
                  <strong>Creating a league:</strong> Choose a name for your league and select the
                  NCAA tournament your league will follow. Set a budget for each team (we recommend
                  $67). After creating the league, you will receive an invite link and a join code
                  that you can share with anyone you want to join. There is no limit on the number
                  of players in a league.
                </p>
                <p className="text-gray-700">
                  <strong>Joining a league:</strong> If someone has shared a league invite link with
                  you, simply click the link and you will be added to the league. If you do not have
                  an account yet, you will be prompted to create one first. The invite process is
                  designed to be as simple as sharing a URL.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Draft Wrestlers to Your Roster
                </h3>
                <p className="text-gray-700 mb-3">
                  Once you are in a league, it is time to build your team. Each team is given a
                  budget for drafting wrestlers. A wrestler's cost is determined by their seed and
                  matches the placement points for that position. For example, a #1 seed costs $16,
                  a #2 seed costs $12, and so on down the line. Wrestlers seeded higher than 8 cost
                  $0.
                </p>
                <p className="text-gray-700 mb-3">
                  You may pick up to 10 wrestlers — one per weight class. You do not have to pick
                  one wrestler for each seed. You may pick multiple wrestlers with the same seed.
                  For instance, you might skip top seeds entirely and instead draft four #4 seeds.
                  Strategy matters here: how you allocate your budget across weight classes can make
                  the difference between winning and losing.
                </p>
                <p className="text-gray-700 mb-3">
                  While rosters are unlocked, each player's team is hidden from other players. Once
                  the roster lock time passes, all teams become visible to everyone in the league.
                </p>
                <p className="text-gray-700">
                  You can view your full roster at any time from your league page. The league sets
                  the roster lock time, and players have until that time to pick their roster.
                </p>
              </div>
            </div>

            {/* Ad Banner - Mid-tutorial placement after step 3 */}
            <div className="my-8">
              <AdBanner slot={AD_SLOTS.howItWorks} format="horizontal" />
            </div>

            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                4
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">The Scoring System</h3>
                <p className="text-gray-700 mb-3">
                  Your team's score is determined by the NCAA team points earned by each wrestler on
                  your roster. Team points include placing, advancement, and bonus points. Penalty
                  deductions for team points will not be deducted from individual wrestlers' team
                  points.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-3">
                  <li>
                    <strong>Placement:</strong> Points are earned based on final placement in the
                    tournament. First place earns the most (16 points), with decreasing points for
                    lower placements. The cost to draft a wrestler matches these placement points —
                    a #1 seed costs $16 because first place earns 16 team points.
                  </li>
                  <li>
                    <strong>Advancement:</strong> Points are earned each time a wrestler advances to
                    the next round of competition. This rewards wrestlers who compete deep into the
                    bracket, regardless of their final placement. If a wrestler receives a bye and
                    wins their next match, they also earn advancement points for the bye.
                  </li>
                  <li>
                    <strong>Bonus:</strong> Additional team points are awarded for pins, technical
                    falls, and major decisions.
                  </li>
                </ul>
                <p className="text-gray-700">
                  Scores are tracked in real time as tournament results come in. You can check your
                  league standings at any point to see where you rank compared to other members.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                5
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Standings and Winning</h3>
                <p className="text-gray-700 mb-3">
                  League standings are based on the cumulative team points earned by each member's
                  roster throughout the tournament. The standings page shows a real-time leaderboard
                  with each member's total score, their roster, and a breakdown of how their points
                  were earned.
                </p>
                <p className="text-gray-700 mb-3">
                  As the tournament progresses through each round, standings shift based on how each
                  member's wrestlers perform. Early leads can change quickly as wrestlers advance
                  deeper into the bracket.
                </p>
                <p className="text-gray-700">
                  The member with the highest total score at the end of the tournament is declared
                  the league champion. Bragging rights are the ultimate prize in Takedown League.
                  There are no entry fees or monetary stakes, keeping the focus on fun, competition,
                  and the sport of wrestling.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Summary</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-3">
                <UserPlusIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Sign Up Free</h3>
              <p className="text-gray-600 text-sm">
                Create your account with email or Google in seconds.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-3">
                <BoltIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Draft Your Team</h3>
              <p className="text-gray-600 text-sm">
                Join a league and draft real wrestlers to your roster.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-3">
                <TrophyIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Compete and Win</h3>
              <p className="text-gray-600 text-sm">
                Earn points from real results and climb the leaderboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {isAuthenticated ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Back to Your Leagues</h2>
              <p className="text-purple-100 mb-6">
                Head back to your leagues to manage your rosters and check standings.
              </p>
              <Link
                to="/leagues"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Back to My Leagues
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Start Competing Today</h2>
              <p className="text-purple-100 mb-6">
                Create your free account and join a league to start drafting your dream roster.
              </p>
              <Link
                to="/login?tab=register"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Create Your Free Account
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
